<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useVModel } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import * as z from 'zod'

import { BrickCountryDropdown, PongoFormCheckbox, PongoFormInput } from '@heypongo/brick'

import BaseDrawer from './BaseDrawer.vue'
import BaseSwipeButton from './BaseSwipeButton.vue'

import type { ContestColor, CustomerForm, GameContest } from './contestGame'

const props = defineProps<{
  apiError: {
    phoneNumber: boolean
    email: boolean
  }
  contestGame: GameContest
  contestColor: ContestColor
  modelValue: boolean
  userForm: CustomerForm
  isProcessing: boolean
  onSubmit?: () => void
}>()
const emit = defineEmits<{
  (e: 'update:apiError', apiError: { phoneNumber: boolean; email: boolean }): void
  (e: 'update:modelValue'): void
  (e: 'update:userForm'): void
  (e: 'on-submit'): void
}>()

const { t } = useI18n()
const route = useRoute()

const isOpen = useVModel(props, 'modelValue', emit)
const form = useVModel(props, 'userForm', emit)

const error = reactive({
  firstName: false,
  email: false,
  phoneNumber: false,
})
const showUnlockToEaseDrawer = ref(false)

setTimeout(() => {
  showUnlockToEaseDrawer.value = true
}, 0)

const selectedStore = computed(() =>
  props.contestGame.stores?.find((store) => store?.store?.slug === route?.params?.storeSlug),
)
const availableCountries = computed(() => {
  const hasAvailableCountries = selectedStore.value && selectedStore.value?.store?.countryStores
  if (hasAvailableCountries) {
    const countries = selectedStore.value?.store?.countryStores
    return countries.map((item) => item.country)
  } else {
    return []
  }
})

const checkForm = () => {
  error.firstName = !form.value.firstName.trim()
  error.email = !z.string().email().safeParse(form.value.email).success
  error.phoneNumber = !form.value.phoneNumber.trim()

  return !error.firstName && !error.email && !error.phoneNumber
}

const onSubmit = async () => {
  if (checkForm()) {
    await props.onSubmit()
  }
}

const hasError = computed(() => {
  return (
    error.firstName ||
    error.email ||
    error.phoneNumber ||
    props.apiError.phoneNumber ||
    props.apiError.email
  )
})

const inputError = (input: 'firstName' | 'email' | 'phoneNumber') => {
  if (error[input]) error[input] = false
}

onMounted(() => {
  form.value.countryCode =
    availableCountries.value.length > 0 ? availableCountries.value[0].alpha2Code : 'FR'
})
</script>

<template>
  <BaseDrawer
    v-model="isOpen"
    :has-blur="false"
    :has-close-icon="false"
    :can-close="false"
    @update:model-value="isOpen = false"
  >
    <template #title>
      <p>{{ contestGame.formSubtitle }}</p>
    </template>
    <template #description>
      <div class="block w-full font-medium">
        <div
          class="-left-4 bottom-10 z-10 hidden max-h-72 w-72 border-transparent bg-gray-50 p-4 py-4 placeholder-gray-400 caret-indigo-300 focus:border-transparent focus:bg-gray-100 focus:text-gray-800"
        />

        <form class="space-y-5 bg-white">
          <div class="flex flex-col">
            <PongoFormInput
              id="firstName"
              v-model="form.firstName"
              name="firstName"
              pattern="b2c"
              :placeholder="t('fields.firstname')"
              @input="inputError('firstName')"
            />
            <p v-if="error.firstName" class="pt-2 text-xs text-red-500">
              {{ $t('errors.emptyFirstName') }}
            </p>
          </div>
          <div class="flex flex-col">
            <div class="flex items-center gap-2">
              <div v-if="availableCountries?.length > 0" class="w-24 rounded-xl bg-gray-50 py-5">
                <BrickCountryDropdown
                  v-model="form.countryCode"
                  :countries="availableCountries"
                  class="mx-auto ml-3"
                  position="top"
                />
              </div>
              <PongoFormInput
                id="phone"
                v-model="form.phoneNumber"
                name="phone"
                type="tel"
                pattern="b2c"
                class="w-full"
                :placeholder="t('fields.phoneNumber')"
                @input="inputError('phoneNumber')"
              />
            </div>
            <p v-if="error.phoneNumber || apiError.phoneNumber" class="pt-2 text-xs text-red-500">
              {{ $t('errors.emptyPhoneNumber') }}
            </p>
          </div>
          <div class="flex flex-col">
            <PongoFormInput
              id="email"
              v-model="form.email"
              name="email"
              type="email"
              pattern="b2c"
              :placeholder="t('fields.email')"
              @input="inputError('email')"
            />
            <p v-if="error.email || apiError.email" class="pt-2 text-xs text-red-500">
              {{ $t('errors.invalidEmail') }}
            </p>
          </div>
          <PongoFormCheckbox
            id="checkbox"
            v-model="form.hasOptin"
            :label="contestGame.optinText"
            :value="form.hasOptin"
          />
          <p v-if="contestGame.conditionsUrl !== null" class="pt-2 text-left text-xs">
            {{ $t('termsOfService.beforeLink') }}
            <a :href="contestGame.conditionsUrl" target="_blank" class="underline">{{
              $t('termsOfService.link')
            }}</a>
            {{ $t('termsOfService.afterLink') }}
          </p>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full px-6 pb-4">
        <BaseSwipeButton
          :text="contestGame.formSubmitButtonText"
          :has-error="hasError"
          :contest-color="contestColor"
          :disabled="isProcessing"
          @on-swipe="onSubmit"
        />
      </div>
    </template>
  </BaseDrawer>
</template>
